import {
  Currency,
  ImprovementValue,
  PerformanceReportComparisonTarget,
  PerformanceReportMetric,
  PerformanceValue,
  PerformanceValueType,
} from '../generated/graphql';

export type ExtractedValue = {
  value: number;
  type?: PerformanceValueType;
  currency?: Currency;
};

export type ExtractedData = {
  target?: string;
  impressions?: ExtractedValue;
  clicks?: ExtractedValue;
  cost_per_click?: ExtractedValue;
  conversions?: ExtractedValue;
  conversion_rate?: ExtractedValue;
  cost_per_action?: ExtractedValue;
  view_through_cost_per_action?: ExtractedValue;
  click_through_rate?: ExtractedValue;
  video_view_rate_p25?: ExtractedValue;
  video_view_rate_p50?: ExtractedValue;
  video_view_rate_p75?: ExtractedValue;
  video_view_rate_p95?: ExtractedValue;
  video_view_rate_p100?: ExtractedValue;
};

// フロント側で計算が必要なmetrics
export const COMPUTED_METRICS = ['conversion_rate'];

export const calcImprovementRate = (actualValue: PerformanceValue, targetValue: PerformanceValue) => {
  if (targetValue.value === 0) return;
  return actualValue.value / targetValue.value - 1;
};

export const extractActualValue = (periodicImprovementValue: ImprovementValue[]): ExtractedData => {
  return periodicImprovementValue.reduce((prev, val) => {
    const { actualValue } = val;

    if (actualValue) {
      const { value, type, currency } = actualValue;
      return {
        ...prev,
        [val.metric]: {
          value,
          type,
          currency: currency || undefined,
        },
      };
    }

    return prev;
  }, {});
};

export const extractImprovementRate = (periodicImprovementValue: ImprovementValue[]): ExtractedData => {
  return periodicImprovementValue.reduce((prev, val) => {
    const { actualValue, targetValue } = val;

    if (actualValue && targetValue) {
      // cv, vtcvが0の場合改善率を計算しない
      if (
        (val.metric === 'cost_per_action' && actualValue.value === 0) ||
        (val.metric === 'view_through_cost_per_action' && actualValue.value === 0)
      ) {
        return prev;
      }
      const improvementRate = calcImprovementRate(actualValue, targetValue);
      if (improvementRate)
        return {
          ...prev,
          [val.metric]: { value: improvementRate, type: 'percentage' as PerformanceValueType },
        };
    }

    return prev;
  }, {});
};

export const computeConversionRate = (
  periodicImprovementValue: ImprovementValue[],
  target: PerformanceReportComparisonTarget,
) => {
  const conversionsValue = periodicImprovementValue.find((val) => val.metric === 'conversions');
  const clicksValue = periodicImprovementValue.find((val) => val.metric === 'clicks');

  if (conversionsValue && clicksValue) {
    const targetValue =
      conversionsValue.targetValue && clicksValue.targetValue && clicksValue.targetValue.value !== 0
        ? {
            type: 'percentage' as PerformanceValueType,
            currency: null,
            value: conversionsValue.targetValue.value / clicksValue.targetValue.value,
          }
        : null;
    const actualValue =
      conversionsValue.actualValue && clicksValue.actualValue && clicksValue.actualValue.value !== 0
        ? {
            type: 'percentage' as PerformanceValueType,
            currency: null,
            value: conversionsValue.actualValue.value / clicksValue.actualValue.value,
          }
        : null;

    const conversionRate = {
      metric: 'conversion_rate' as PerformanceReportMetric,
      target,
      targetValue,
      actualValue,
    };

    return conversionRate;
  }
  return;
};

export const computeMetricsValues = (
  periodicImprovementValue: ImprovementValue[],
  target: PerformanceReportComparisonTarget,
) => {
  const result = [...periodicImprovementValue];
  const conversionRate = computeConversionRate(periodicImprovementValue, target);
  if (conversionRate) result.push(conversionRate);

  return result;
};
